import styled from "styled-components"

import { Banner, spacing } from "@minuthq/meatball-ui-react"
import { Text } from "@minuthq/meatball-ui-react"

import { UserOrderStatusBadge } from "src/components/Account/BillingPortal/UserOrders/UserOrderStatusBadge"
import { TUserOrderDetails } from "src/data/billing/types/billingTypes"
import { usePostFetchInvoicePdf } from "src/data/invoices/queries/InvoiceQueries"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { MButton } from "src/ui/Button/MButton"
import { ExternalLink } from "src/ui/Link/ExternalLink"
import { downloadFromUrl } from "src/utils/downloadUtil"
import { formatDate } from "src/utils/l10n"

export function UserOrderInformation({ order }: { order: TUserOrderDetails }) {
  const { t, langKeys } = useTranslate()
  const user = useGetUser()
  const postFetchInvoicePdf = usePostFetchInvoicePdf()

  function handleDownloadInvoiceClick(invoiceId: string) {
    postFetchInvoicePdf.mutate(
      { invoiceId },
      {
        onSuccess: (url) => {
          downloadFromUrl(url)
        },
      }
    )
  }

  return (
    <div>
      <Text variant="heading3" marginBottom={spacing.XS}>
        {t(langKeys.orders_details_order_info_title)}
      </Text>

      <DescriptionBox>
        <Text color="secondary">
          {t(langKeys.date)}:{" "}
          {formatDate({
            date: order.created_at,
            clockType: user.clock_type,
          })}
        </Text>

        <Text color="secondary">
          {t(langKeys.status)}: <UserOrderStatusBadge status={order.status} />
        </Text>

        <Text color="secondary">
          {t(langKeys.orders_tracking)}:{" "}
          {order.tracking_url ? (
            <ExternalLink href={order.tracking_url}>
              {t(langKeys.orders_view_tracking)}{" "}
            </ExternalLink>
          ) : (
            t(langKeys.orders_tracking_not_available)
          )}
        </Text>

        {order.invoice_id && (
          <Text color="secondary">
            {t(langKeys.invoice)}:{" "}
            <MButton
              variant="minimal"
              loading={postFetchInvoicePdf.isLoading}
              onClick={() => handleDownloadInvoiceClick(order.invoice_id || "")}
            >
              {t(langKeys.download)}
            </MButton>
          </Text>
        )}
      </DescriptionBox>
      {order.partial_fulfillment && (
        <PartialFulfillmentBanner type="info">
          {t(langKeys.orders_partially_fulfilled_note)}
        </PartialFulfillmentBanner>
      )}
    </div>
  )
}

const PartialFulfillmentBanner = styled(Banner)`
  margin-top: ${spacing.L};
`

const DescriptionBox = styled.div`
  display: grid;
  gap: ${spacing.XS2};
`
